/**
 * InstanceStatusBadge — status pill for the running sandbox instance.
 * Mirrors SandboxStatusBadge (template-level) but reads the instance-level
 * lifecycle enum. The two badges sit side-by-side in the sandbox detail
 * header so users can see template vs instance state at a glance.
 */
import React from 'react';
import { PulsingDot, TagBadge, type TagColor } from '@chatsdet/ui';
import type { EnvironmentInstanceStatus } from '../../../hooks/useEnvironmentInstances';

type Config = {
  label: string;
  color: TagColor;
  transitional: boolean;
};

const STATUS_CONFIG: Record<EnvironmentInstanceStatus, Config> = {
  pending: { label: 'Pending', color: 'gray', transitional: true },
  provisioning: { label: 'Provisioning', color: 'blue', transitional: true },
  deploying: { label: 'Deploying', color: 'blue', transitional: true },
  running: { label: 'Running', color: 'green', transitional: false },
  converting: { label: 'Converting', color: 'blue', transitional: true },
  stopping: { label: 'Stopping', color: 'yellow', transitional: true },
  terminated: { label: 'Terminated', color: 'gray', transitional: false },
  failed: { label: 'Failed', color: 'red', transitional: false },
};

const DOT_BG: Record<TagColor, string> = {
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  yellow: 'bg-amber-500',
  green: 'bg-emerald-500',
  teal: 'bg-teal-500',
  blue: 'bg-blue-500',
  indigo: 'bg-indigo-500',
  purple: 'bg-purple-500',
  pink: 'bg-pink-500',
  gray: 'bg-slate-400',
};

export interface InstanceStatusBadgeProps {
  status: EnvironmentInstanceStatus;
  /** Prefix label rendered before the status (e.g. "Instance"). Defaults to none. */
  prefix?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function InstanceStatusBadge({ status, prefix, size = 'md' }: InstanceStatusBadgeProps) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending;
  const label = prefix ? `${prefix} ${cfg.label}` : cfg.label;
  return (
    <TagBadge
      name={label}
      color={cfg.color}
      size={size}
      icon={cfg.transitional ? <PulsingDot colorClassName={DOT_BG[cfg.color]} sizeClassName="h-1.5 w-1.5" /> : undefined}
      hideDot={!cfg.transitional}
    />
  );
}

InstanceStatusBadge.displayName = 'InstanceStatusBadge';
